import React from 'react'

type PeriodValue = 'all' | `${number}`

function getPeriodOptions(
    startYear = new Date().getFullYear(),
    endYear = 2020
) {
    const top = Math.max(startYear, endYear)
    const bottom = Math.min(startYear, endYear)
    const years: `${number}`[] = []
    for (let y = top; y >= bottom; y--) years.push(String(y) as `${number}`)
    return [...years, 'all'] as PeriodValue[]
}

interface RegistryPeriodSelectProps {
    startYear?: number // верхняя граница (по умолчанию текущий год)
    endYear?: number // нижняя граница (по умолчанию 2020)
    value?: PeriodValue // для контролируемого режима
    defaultValue?: PeriodValue // начальное значение для неконтролируемого
    onChange?: (v: PeriodValue) => void
    className?: string
}

const RegistryPeriodSelect: React.FC<RegistryPeriodSelectProps> = ({
    startYear = new Date().getFullYear(),
    endYear = 2020,
    value,
    defaultValue,
    onChange,
    className,
}) => {
    const options = React.useMemo(
        () => getPeriodOptions(startYear, endYear),
        [startYear, endYear]
    )
    const currentYear = String(new Date().getFullYear()) as `${number}`

    // Если value не передан — работаем во внутреннем режиме
    const isControlled = value !== undefined
    const initial =
        defaultValue ??
        (options.includes(currentYear) ? currentYear : options[0])

    const [inner, setInner] = React.useState<PeriodValue>(initial)

    // Обновляем внутреннее значение, если изменился список опций (год сменился и т. п.)
    React.useEffect(() => {
        if (!isControlled) {
            const next = options.includes(inner)
                ? inner
                : options.includes(currentYear)
                  ? currentYear
                  : options[0]
            if (next !== inner) setInner(next)
        }
    }, [options, currentYear, inner, isControlled])

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const v = e.target.value as PeriodValue
        if (!isControlled) setInner(v)
        onChange?.(v)
    }

    return (
        <select
            value={isControlled ? value! : inner}
            onChange={handleChange}
            className={className ?? 'form-select'}
        >
            {options.map((opt) =>
                opt === 'all' ? (
                    <option key="all" value="all">
                        За все время
                    </option>
                ) : (
                    <option key={opt} value={opt}>
                        {opt}
                    </option>
                )
            )}
        </select>
    )
}

export default RegistryPeriodSelect
